import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  MenuBook as PlaybookIcon,
  Timer as SlaIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';
import { minutesToHuman } from '../Cases/SlaBadge';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const DEFAULT_TARGETS = [
  { severity: 'Critical', responseTime: 30, resolutionTime: 4 * 60 },
  { severity: 'High', responseTime: 2 * 60, resolutionTime: 24 * 60 },
  { severity: 'Medium', responseTime: 8 * 60, resolutionTime: 3 * 24 * 60 },
  { severity: 'Low', responseTime: 24 * 60, resolutionTime: 7 * 24 * 60 },
];

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const localId = () => `new-${Math.random().toString(36).slice(2, 10)}`;
const emptyPlaybook = () => ({ id: localId(), name: '', category: '', description: '', tasks: [] });

/** Normalise SLA config so every severity has a row, in canonical order. */
const normaliseSla = (sla) => {
  const targets = Array.isArray(sla?.targets) ? sla.targets : [];
  return {
    enabled: sla ? sla.enabled !== false : true,
    targets: SEVERITIES.map((severity) => {
      const found = targets.find((t) => t.severity === severity) || DEFAULT_TARGETS.find((t) => t.severity === severity);
      return { severity, responseTime: found.responseTime, resolutionTime: found.resolutionTime };
    }),
  };
};

const moveItem = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/* ------------------------------------------------------------------ */
/* Playbook editor dialog                                              */
/* ------------------------------------------------------------------ */
const PlaybookDialog = ({ open, playbook, categories, onClose, onSave, saving }) => {
  const [form, setForm] = useState(emptyPlaybook());
  const [newTask, setNewTask] = useState({ title: '', description: '' });

  useEffect(() => {
    if (open) {
      setForm(playbook ? { ...playbook, tasks: (playbook.tasks || []).map((t) => ({ title: t.title || '', description: t.description || '' })) } : emptyPlaybook());
      setNewTask({ title: '', description: '' });
    }
  }, [open, playbook]);

  const setTask = (idx, patch) => setForm((f) => ({ ...f, tasks: f.tasks.map((t, i) => (i === idx ? { ...t, ...patch } : t)) }));
  const removeTask = (idx) => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }));
  const move = (idx, dir) => setForm((f) => ({ ...f, tasks: moveItem(f.tasks, idx, idx + dir) }));
  const addTask = () => {
    const title = newTask.title.trim();
    if (!title) return;
    setForm((f) => ({ ...f, tasks: [...f.tasks, { title, description: newTask.description.trim() }] }));
    setNewTask({ title: '', description: '' });
  };

  const categoryOptions = form.category && !categories.includes(form.category) ? [form.category, ...categories] : categories;
  const valid = form.name.trim().length > 0 && form.tasks.some((t) => t.title.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{playbook ? 'Edit playbook' : 'New playbook'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required disabled={saving} />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth disabled={saving}>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={form.category || ''} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                <MenuItem value=""><em>Any</em></MenuItem>
                {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description" value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={saving} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Tasks ({form.tasks.length})</Typography>
        {form.tasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Add at least one task.</Typography>
        )}
        <List dense disablePadding>
          {form.tasks.map((task, idx) => (
            <ListItem key={idx} disableGutters sx={{ gap: 1, alignItems: 'flex-start' }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 24, pt: 1.5, textAlign: 'right' }}>{idx + 1}.</Typography>
              <Box flex={1} display="flex" flexDirection="column" gap={1}>
                <TextField size="small" fullWidth label="Title" value={task.title} onChange={(e) => setTask(idx, { title: e.target.value })} disabled={saving} />
                <TextField size="small" fullWidth label="Description (optional)" value={task.description} onChange={(e) => setTask(idx, { description: e.target.value })} disabled={saving} />
              </Box>
              <Box display="flex" flexDirection="column">
                <Tooltip title="Move up"><span><IconButton size="small" onClick={() => move(idx, -1)} disabled={saving || idx === 0}><UpIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Move down"><span><IconButton size="small" onClick={() => move(idx, 1)} disabled={saving || idx === form.tasks.length - 1}><DownIcon fontSize="small" /></IconButton></span></Tooltip>
              </Box>
              <Tooltip title="Remove"><span><IconButton size="small" color="error" onClick={() => removeTask(idx)} disabled={saving} sx={{ mt: 0.5 }}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label="New task title"
            value={newTask.title}
            onChange={(e) => setNewTask((t) => ({ ...t, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
            sx={{ flex: '1 1 220px' }}
            disabled={saving}
          />
          <TextField
            size="small"
            label="Description (optional)"
            value={newTask.description}
            onChange={(e) => setNewTask((t) => ({ ...t, description: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
            sx={{ flex: '1 1 220px' }}
            disabled={saving}
          />
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addTask} disabled={saving || !newTask.title.trim()}>Add task</Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={() => onSave({ ...form, name: form.name.trim(), tasks: form.tasks.filter((t) => t.title.trim()).map((t) => ({ title: t.title.trim(), description: t.description.trim() })) })} disabled={saving || !valid}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Workflows settings                                                   */
/* ------------------------------------------------------------------ */
const Workflows = () => {
  const { canManageCompany } = usePermission();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [playbooks, setPlaybooks] = useState([]);
  const [sla, setSla] = useState(normaliseSla(null));
  const [slaDirty, setSlaDirty] = useState(false);
  const [categories, setCategories] = useState([]);

  const [savingPlaybooks, setSavingPlaybooks] = useState(false);
  const [savingSla, setSavingSla] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [wf, opts] = await Promise.all([
        apiService.workflows.get(),
        apiService.cases.getOptions().catch(() => ({ data: {} })),
      ]);
      setPlaybooks(wf.data?.playbooks || []);
      setSla(normaliseSla(wf.data?.sla));
      setSlaDirty(false);
      setCategories(Array.isArray(opts.data?.categories) ? opts.data.categories : []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load workflow settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!success) return undefined;
    const handle = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(handle);
  }, [success]);

  const persistPlaybooks = async (next, message) => {
    try {
      setSavingPlaybooks(true);
      setError(null);
      const res = await apiService.workflows.update({ playbooks: next });
      setPlaybooks(res.data?.playbooks || next);
      setSuccess(message);
      return true;
    } catch (err) {
      setError(serverMessage(err, 'Failed to save playbooks'));
      return false;
    } finally {
      setSavingPlaybooks(false);
    }
  };

  const handleSavePlaybook = async (form) => {
    const exists = playbooks.some((p) => p.id === form.id);
    // New playbooks carry a temporary local id; drop it so the server assigns a real one.
    const { id: localOnlyId, ...withoutId } = form;
    const next = exists ? playbooks.map((p) => (p.id === form.id ? form : p)) : [...playbooks, withoutId];
    const ok = await persistPlaybooks(next, exists ? 'Playbook updated' : 'Playbook created');
    if (ok) { setDialogOpen(false); setEditingPlaybook(null); }
  };

  const handleDeletePlaybook = (pb) => {
    if (!window.confirm(`Delete playbook "${pb.name}"? Tasks already applied to cases are kept.`)) return;
    persistPlaybooks(playbooks.filter((p) => p.id !== pb.id), 'Playbook deleted');
  };

  const handleMovePlaybook = (idx, dir) => persistPlaybooks(moveItem(playbooks, idx, idx + dir), 'Playbook order updated');

  const setTarget = (severity, field, value) => {
    setSla((s) => ({ ...s, targets: s.targets.map((t) => (t.severity === severity ? { ...t, [field]: value } : t)) }));
    setSlaDirty(true);
  };

  const handleSaveSla = async () => {
    const targets = sla.targets.map((t) => ({
      severity: t.severity,
      responseTime: Math.max(1, parseInt(t.responseTime, 10) || 0),
      resolutionTime: Math.max(1, parseInt(t.resolutionTime, 10) || 0),
    }));
    const invalid = targets.find((t) => t.resolutionTime < t.responseTime);
    if (invalid) { setError(`${invalid.severity}: resolution time must be at least the response time.`); return; }
    try {
      setSavingSla(true);
      setError(null);
      const res = await apiService.workflows.update({ sla: { enabled: sla.enabled, targets } });
      setSla(normaliseSla(res.data?.sla || { enabled: sla.enabled, targets }));
      setSlaDirty(false);
      setSuccess('SLA targets saved');
    } catch (err) {
      setError(serverMessage(err, 'Failed to save SLA targets'));
    } finally {
      setSavingSla(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!canManageCompany && (
        <Alert severity="info" sx={{ mb: 2 }}>You can view playbooks and SLA targets, but only company managers can change them.</Alert>
      )}

      {/* Playbooks */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<PlaybookIcon color="primary" />}
          title="Playbooks"
          subheader="Reusable task checklists analysts can apply to a case"
          action={canManageCompany && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setEditingPlaybook(null); setDialogOpen(true); }} disabled={savingPlaybooks}>
              New playbook
            </Button>
          )}
        />
        <CardContent>
          {playbooks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No playbooks defined yet.</Typography>
          ) : (
            <List disablePadding>
              {playbooks.map((pb, idx) => (
                <ListItem
                  key={pb.id}
                  divider={idx < playbooks.length - 1}
                  disableGutters
                  secondaryAction={canManageCompany ? (
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Move up"><span><IconButton size="small" onClick={() => handleMovePlaybook(idx, -1)} disabled={savingPlaybooks || idx === 0}><UpIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Move down"><span><IconButton size="small" onClick={() => handleMovePlaybook(idx, 1)} disabled={savingPlaybooks || idx === playbooks.length - 1}><DownIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Edit"><span><IconButton size="small" onClick={() => { setEditingPlaybook(pb); setDialogOpen(true); }} disabled={savingPlaybooks}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Delete"><span><IconButton size="small" color="error" onClick={() => handleDeletePlaybook(pb)} disabled={savingPlaybooks}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                    </Box>
                  ) : null}
                  sx={{ pr: canManageCompany ? 20 : 0 }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1">{pb.name}</Typography>
                        {pb.category && <Chip label={pb.category} size="small" variant="outlined" />}
                        <Chip label={`${(pb.tasks || []).length} task${(pb.tasks || []).length === 1 ? '' : 's'}`} size="small" color="primary" variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <Box component="span" display="block">
                        {pb.description && <Typography component="span" variant="body2" color="text.secondary" display="block">{pb.description}</Typography>}
                        <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {(pb.tasks || []).slice(0, 3).map((t) => t.title).join(' · ')}
                          {(pb.tasks || []).length > 3 ? ` · +${pb.tasks.length - 3} more` : ''}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* SLA */}
      <Card>
        <CardHeader
          avatar={<SlaIcon color="primary" />}
          title="SLA targets"
          subheader="Response and resolution targets per severity, measured from case creation"
          action={(
            <FormControlLabel
              control={<Switch checked={sla.enabled} onChange={(e) => { setSla((s) => ({ ...s, enabled: e.target.checked })); setSlaDirty(true); }} disabled={!canManageCompany || savingSla} />}
              label={sla.enabled ? 'Enabled' : 'Disabled'}
            />
          )}
        />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Response time (minutes)</TableCell>
                  <TableCell>Resolution time (minutes)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sla.targets.map((t) => (
                  <TableRow key={t.severity}>
                    <TableCell><Typography variant="body2" fontWeight="medium">{t.severity}</Typography></TableCell>
                    {['responseTime', 'resolutionTime'].map((field) => (
                      <TableCell key={field}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <TextField
                            size="small"
                            type="number"
                            value={t[field]}
                            onChange={(e) => setTarget(t.severity, field, e.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                            disabled={!canManageCompany || !sla.enabled || savingSla}
                            sx={{ width: 120 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{minutesToHuman(t[field])}</Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {canManageCompany && (
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button onClick={() => load()} disabled={savingSla || !slaDirty}>Reset</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSla} disabled={savingSla || !slaDirty}>
                {savingSla ? 'Saving...' : 'Save SLA'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <PlaybookDialog
        open={dialogOpen}
        playbook={editingPlaybook}
        categories={categories}
        onClose={() => { setDialogOpen(false); setEditingPlaybook(null); }}
        onSave={handleSavePlaybook}
        saving={savingPlaybooks}
      />
    </Box>
  );
};

export default Workflows;
